import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface SaveConfirmationModalProps {
    isVisible: boolean;
    onSave: () => void;
    onDiscard: () => void;
    onCancel: () => void;
    isSaving?: boolean;
}

export const SaveConfirmationModal: React.FC<SaveConfirmationModalProps> = ({
    isVisible,
    onSave,
    onDiscard,
    onCancel,
    isSaving = false
}) => {
    const theme = useTheme();

    if (!isVisible) return null;

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
        },
        modal: {
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            maxWidth: '400px',
            width: '90%',
            boxShadow: theme.shadows.lg,
            overflow: 'hidden',
        },
        header: {
            backgroundColor: theme.colors.primary,
            padding: theme.spacing.xl,
        },
        title: {
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.colors.text.inverse,
            margin: 0,
        },
        body: {
            padding: theme.spacing.xl,
        },
        message: {
            fontSize: theme.typography.fontSize.md,
            color: theme.colors.text.secondary,
            marginBottom: theme.spacing.xl,
            lineHeight: 1.5,
        },
        buttons: {
            display: 'flex',
            gap: theme.spacing.sm,
            justifyContent: 'flex-end',
        },
        button: {
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: theme.borderRadius.sm,
            border: 'none',
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: `all ${theme.transitions.normal}`,
        },
        saveButton: {
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.inverse,
        },
        discardButton: {
            backgroundColor: theme.colors.status.error,
            color: theme.colors.text.inverse,
        },
        cancelButton: {
            backgroundColor: 'transparent',
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.default}`,
        },
        disabledButton: {
            opacity: 0.6,
            cursor: 'not-allowed',
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Unsaved Changes</h2>
                </div>
                <div style={styles.body}>
                <p style={styles.message}>
                    You have unsaved changes that will be lost. What would you like to do?
                </p>
                <div style={styles.buttons}>
                    <button
                        style={{
                            ...styles.button,
                            ...styles.cancelButton,
                            ...(isSaving ? styles.disabledButton : {})
                        }}
                        onClick={onCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        style={{
                            ...styles.button,
                            ...styles.discardButton,
                            ...(isSaving ? styles.disabledButton : {})
                        }}
                        onClick={onDiscard}
                        disabled={isSaving}
                    >
                        Discard Changes
                    </button>
                    <button
                        style={{
                            ...styles.button,
                            ...styles.saveButton,
                            ...(isSaving ? styles.disabledButton : {})
                        }}
                        onClick={onSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save & Continue'}
                    </button>
                </div>
                </div>
            </div>
        </div>
    );
};
